import { TheWestSection } from '@news-mono/common'
import {
    AddToCartEvent,
    BreachEvent,
    BreachScreenContext,
    click_origin_breach_screen,
    useFeature,
    useSubscribeWithGoogle,
} from '@news-mono/web-common'
import React, { useContext, useRef } from 'react'
import { IconStar } from '../../../../../../icons/IconStar/IconStar'
import { useAuthActions } from '../../../../../../user-registration/authentication/AuthenticationProvider'
import { useSection, useSectionMetaInfo } from '../../../../../../__helpers'
import { getSubscriptionValue } from '../../../../common'
import { createLinkClickEvent } from '../../../../createLinkClickHandler'
import {
    Ribbon,
    StyledPackageImage,
    StyledSubscriptionCancellationMessage,
    StyledSubscriptionPackage,
    StyledSubscriptionPackageActions,
    StyledSubscriptionPackageButton,
    StyledSubscriptionPackageHeading,
    StyledSubscriptionPackageOriginalPrice,
    StyledSubscriptionPackagePreTitle,
    StyledSubscriptionPackagePrice,
    StyledSubscriptionPackagePriceQualifier,
} from './BlackFridaySubscriptionPackage.styled'

const everyDayDigitalImage = require('../../../SubscriptionPackage/images/updated2022/everyday_digital.png')
const everyDayNewspaperImage = require('../../../SubscriptionPackage/images/updated2022/Weekend-Papers-plus-Everyday-Digita_Mobile.png')

export interface BlackFridaySubscriptionPackageProps {
    pretitle: string
    originalPrice?: string
    heading: string
    price: string
    priceQualifier?: string
    ribbonTitle?: string
    packagePath: string
    offerCode?: string
    rateCode: string
    weeklyPrice: number
    weeklyDiscount?: number
    packageType?: 'digital' | 'digitalprint'
    breachCreativeName: string
    breachDeal: string
    packageTheme?: 'default' | 'red'
    cancellationMessage?: string
    googleSKU?: string
    masthead: string
    onEvent: (event: BreachEvent) => void
}

export const BlackFridaySubscriptionPackage: React.FC<BlackFridaySubscriptionPackageProps> =
    ({
        pretitle,
        heading,
        originalPrice,
        price,
        priceQualifier,
        ribbonTitle,
        packagePath,
        breachDeal,
        offerCode,
        rateCode,
        weeklyPrice,
        weeklyDiscount,
        breachCreativeName,
        packageTheme = 'default',
        onEvent,
        packageType,
        cancellationMessage,
        googleSKU,
        masthead,
    }) => {
        const { onSubscribeClick } = useAuthActions()
        const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
        const section = useSection<TheWestSection>()

        const sectionName = section === 'genwest' ? 'default' : section

        // Matches swg-breach-albany-advertiser etc
        const subscribeWithGoogleBreachInterfaceEnabled = useFeature(
            `swg-breach-${sectionName}`,
        )

        const buttonRef = useRef<HTMLButtonElement>(null)

        const showSwG =
            subscribeWithGoogleEnabled &&
            subscribeWithGoogleBreachInterfaceEnabled &&
            googleSKU

        const sectionMeta = useSectionMetaInfo()

        const breachScreenContext = useContext(BreachScreenContext)
        const breachContext = breachScreenContext.context

        useSubscribeWithGoogle(
            buttonRef.current as HTMLButtonElement,
            googleSKU || 'everyday_digital_4weeks',
            sectionMeta,
            breachContext,
        )

        const addToCartPayload: AddToCartEvent['payload'] = {
            currency: 'AUD',
            item_id: rateCode,
            item_brand: masthead,
            item_name: heading,
            price: getSubscriptionValue(weeklyPrice),
            quantity: 1,
            coupon: offerCode,
            discount: getSubscriptionValue(weeklyDiscount),
            item_variant: 'native',
        }

        return (
            // Note: There is high editorial care factor on these images, please ensure
            // appropriate approvals have happened before replacing or updating
            <StyledSubscriptionPackage packageTheme={packageTheme}>
                {packageType === 'digital' && (
                    <StyledPackageImage
                        src={everyDayDigitalImage}
                        alt=""
                        role="presentation"
                    />
                )}

                {packageType === 'digitalprint' && (
                    <StyledPackageImage
                        src={everyDayNewspaperImage}
                        alt=""
                        role="presentation"
                    />
                )}
                <StyledSubscriptionPackagePreTitle packageTheme={packageTheme}>
                    {pretitle}
                </StyledSubscriptionPackagePreTitle>
                <StyledSubscriptionPackageHeading packageTheme={packageTheme}>
                    {heading}
                </StyledSubscriptionPackageHeading>
                {originalPrice && (
                    <StyledSubscriptionPackageOriginalPrice
                        packageTheme={packageTheme}
                    >
                        {originalPrice}
                    </StyledSubscriptionPackageOriginalPrice>
                )}
                <StyledSubscriptionPackagePrice packageTheme={packageTheme}>
                    {price}
                </StyledSubscriptionPackagePrice>
                {priceQualifier && (
                    <StyledSubscriptionPackagePriceQualifier
                        packageTheme={packageTheme}
                    >
                        {priceQualifier}
                    </StyledSubscriptionPackagePriceQualifier>
                )}
                {ribbonTitle && (
                    <Ribbon packageTheme={packageTheme}>
                        <span>
                            <IconStar />
                            {ribbonTitle}
                        </span>
                    </Ribbon>
                )}
                <StyledSubscriptionPackageActions>
                    <StyledSubscriptionPackageButton
                        packageTheme={packageTheme}
                        buttonWidth="wide"
                        onClick={() => {
                            createLinkClickEvent(
                                breachCreativeName,
                                breachDeal,
                                onEvent,
                                undefined,
                                1,
                                addToCartPayload,
                            )()
                            onSubscribeClick({
                                packagePath,
                                offerCode,
                                callToAction: click_origin_breach_screen,
                            })
                        }}
                    >
                        Subscribe Now
                    </StyledSubscriptionPackageButton>
                    <StyledSubscriptionCancellationMessage
                        packageTheme={packageTheme}
                    >
                        {cancellationMessage}
                    </StyledSubscriptionCancellationMessage>
                    {/* {packageType === 'digital' && showSwG && (
                        <StyledSubscribeWithGoogleButton ref={buttonRef} />
                    )} */}
                </StyledSubscriptionPackageActions>
            </StyledSubscriptionPackage>
        )
    }

BlackFridaySubscriptionPackage.displayName = 'BlackFridaySubscriptionPackage'

export default BlackFridaySubscriptionPackage
